// src/components/ExpenseSection.js (With Pagination & Monthly Totals)

import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';

function ExpenseSection() {
    const { user, allStations, currentStationId } = useAppContext();
    const [expenses, setExpenses] = useState([]);
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [category, setCategory] = useState('Miscellaneous'); 
    const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    
    const [monthlyTotal, setMonthlyTotal] = useState(0);

    // --- NEW STATE FOR PAGINATION ---
    const [pageSize, setPageSize] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalExpenses, setTotalExpenses] = useState(0);

    const expenseCategories = ['Miscellaneous', 'Utilities', 'Marketing', 'Rent', 'Office Supplies', 'Salaries'];

    const fetchExpenses = useCallback(async () => {
        if (!currentStationId || currentStationId === 'all') {
            setExpenses([]); setIsLoading(false); return;
        }
        setIsLoading(true);

        const startOfMonth = new Date(filterYear, filterMonth - 1, 1).toISOString().split('T')[0];
        const endOfMonth = new Date(filterYear, filterMonth, 0).toISOString().split('T')[0];

        try {
            const results = await api.get('expenses', { 
                where: { stationId: currentStationId }
            });

            const filtered = results.filter(exp => {
                const d = new Date(exp.date).toISOString().split('T')[0];
                return d >= startOfMonth && d <= endOfMonth;
            });

            setTotalExpenses(filtered.length);
            
            // Manual pagination
            const start = (currentPage - 1) * pageSize;
            const end = start + pageSize;
            setExpenses(filtered.slice(start, end));

            // Calculate monthly total
            const total = filtered.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
            setMonthlyTotal(total);

        } catch (err) {
            toast.error("Error fetching expenses.");
        } finally {
            setIsLoading(false);
        }
    }, [currentStationId, filterYear, filterMonth, pageSize, currentPage]);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description || !amount || !expenseDate || !currentStationId || currentStationId === 'all') {
            toast.error("Please fill all required fields and select a valid station."); return;
        }

        setIsSubmitting(true);
        try {
            await api.post('expenses', {
                description, amount: parseFloat(amount), category,
                stationId: currentStationId, date: expenseDate,
                createdAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
            });
            toast.success("Expense added successfully.");
            setDescription(''); setAmount(''); setCategory('Miscellaneous');
            fetchExpenses();
        } catch (e) {
            toast.error(`Error adding expense: ${e.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleDelete = async () => {
        if (!deletingId) return;
        try {
            await api.delete('expenses', deletingId);
            toast.success("Expense deleted successfully.");
            fetchExpenses();
        } catch (e) {
            toast.error(`Error deleting expense: ${e.message}`);
        } finally {
            setDeletingId(null);
        }
    };
    
    const pageStart = totalExpenses > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const pageEnd = Math.min(currentPage * pageSize, totalExpenses);

    return (
        <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingId} onClose={() => setDeletingId(null)} onConfirm={handleDelete} title="Delete Expense">Are you sure you want to delete this expense? This action is irreversible.</ConfirmationModal>
            <div className="mb-8"><h1 className="text-4xl font-bold text-gray-800">Expense Management</h1><p className="text-gray-500 mt-1">Track and manage all your operational expenses.</p></div>
            
            {!currentStationId || currentStationId === 'all' ? (<div className="text-center p-10 bg-white rounded-2xl shadow-lg"><h3 className="text-xl font-bold text-gray-700">Please select a station</h3><p className="text-gray-500 mt-2">Select a station to manage expenses.</p></div>)
            : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-lg p-8 sticky top-8">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">New Expense Entry</h2>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div><label className="block text-sm font-medium text-gray-700">Description</label><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., Office Lunch" className="mt-1 w-full p-3 bg-gray-50 rounded-md" required /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-sm font-medium text-gray-700">Amount</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="PKR" className="mt-1 w-full p-3 bg-gray-50 rounded-md" required /></div>
                                    <div><label className="block text-sm font-medium text-gray-700">Date</label><input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md" required /></div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Category</label>
                                    <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-md">
                                        {expenseCategories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <button type="submit" disabled={isSubmitting} className="w-full bg-rose-600 text-white font-bold py-4 rounded-lg shadow-lg hover:bg-rose-700 disabled:bg-gray-400">{isSubmitting ? "Saving..." : "Add Expense"}</button>
                            </form>
                        </div>
                    </div>
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="p-6 border-b"><div className="flex flex-wrap justify-between items-center gap-4"><h2 className="text-2xl font-bold text-gray-800">Expense History</h2><div className="flex gap-4"><select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="p-2 border rounded-md bg-gray-50">{Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select><select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="p-2 border rounded-md bg-gray-50">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select></div></div></div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50"><tr><th className="px-6 py-3">Date</th><th className="px-6 py-3">Details</th><th className="px-6 py-3 hidden md:table-cell">Station</th><th className="px-6 py-3 text-right">Amount</th>{user.role === 'admin' && <th className="px-6 py-3 text-center">Actions</th>}</tr></thead>
                                <tbody>
                                    {isLoading ? (<tr><td colSpan={user.role === 'admin' ? 5 : 4} className="text-center p-8">Loading...</td></tr>) 
                                    : expenses.length > 0 ? expenses.map(exp => (
                                        <tr key={exp.id} className="bg-white border-b hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-600">{new Date(exp.date).toLocaleDateString()}</td>
                                            <td className="px-6 py-4"><div className="font-semibold text-gray-800">{exp.description}</div><div className="text-xs text-gray-500">{exp.category}</div></td>
                                            <td className="px-6 py-4 hidden md:table-cell">{allStations.find(s => s.id === exp.stationId)?.name || 'N/A'}</td>
                                            <td className="px-6 py-4 text-right font-bold text-gray-800">PKR {(parseFloat(exp.amount) || 0).toLocaleString()}</td>
                                            {user.role === 'admin' && (<td className="px-6 py-4 text-center"><button onClick={() => setDeletingId(exp.id)} className="text-red-600 hover:text-red-800 font-semibold no-underline">Delete</button></td>)}
                                        </tr>
                                    )) : (<tr><td colSpan={user.role === 'admin' ? 5 : 4} className="text-center p-8">No expenses found for this period.</td></tr>)}
                                </tbody>
                                <tfoot className="bg-gray-100 font-bold"><tr className="text-gray-900"><td colSpan={user.role === 'admin' ? 4 : 3} className="px-6 py-4 text-right">Total for this month:</td><td className="px-6 py-4 text-right font-mono text-xl">PKR {monthlyTotal.toLocaleString()}</td>{user.role === 'admin' && <td className="px-6 py-4"></td>}</tr></tfoot>
                            </table>
                        </div>
                         {totalExpenses > pageSize && (
                            <div className="p-4 flex justify-between items-center bg-white rounded-b-2xl border-t">
                                <div className="flex items-center gap-2"><label htmlFor="pageSizeExp" className="text-sm font-medium text-gray-700">Show:</label><select id="pageSizeExp" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="p-2 bg-gray-50 rounded-md"><option value="15">15</option><option value="30">30</option><option value="50">50</option></select></div>
                                <span className="text-sm font-medium text-gray-700">Showing {pageStart} - {pageEnd} of {totalExpenses}</span>
                                <div className="flex gap-2"><button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1 || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 rounded-md shadow disabled:bg-gray-300">Previous</button><button onClick={() => setCurrentPage(p => p + 1)} disabled={pageEnd >= totalExpenses || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 rounded-md shadow disabled:bg-gray-300">Next</button></div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ExpenseSection;
